/**
 * @fileoverview Overall component for the Launch Task Flow display and navigation.
 *
 * @author Wyatt Lindquist (wlindquist@yext.com)
 */
import {Routes} from '/src/com/yext/listingslaunchstorm/js/routes';

import {SelectEntitiesTask} from '/src/com/yext/listingslaunchstorm/js/enterprise/selectentities/selectentitiestask';
import {SelectFeaturesTask} from '/src/com/yext/listingslaunchstorm/js/enterprise/selectfeatures/selectfeaturestask';
import {AppleLinkedAccountTask} from '/src/com/yext/listingslaunchstorm/js/enterprise/setuppublisher/apple/applelinkedaccounttask';
import {ApplePublisherId} from '/src/com/yext/listingslaunchstorm/js/enterprise/setuppublisher/data/appleconfiguration';
import {FacebookPublisherId} from '/src/com/yext/listingslaunchstorm/js/enterprise/setuppublisher/data/facebookconfiguration';
import {GooglePublisherId} from '/src/com/yext/listingslaunchstorm/js/enterprise/setuppublisher/data/gbpconfiguration';
import {YelpEnterprisePublisherId, YelpKnowledgePublisherId} from '/src/com/yext/listingslaunchstorm/js/enterprise/setuppublisher/data/yelpconfiguration';
import {FacebookLinkedAccountTask} from '/src/com/yext/listingslaunchstorm/js/enterprise/setuppublisher/facebook/facebooklinkedaccounttask';
import {FacebookSyncedFieldsTask} from '/src/com/yext/listingslaunchstorm/js/enterprise/setuppublisher/facebook/facebooksyncedfieldstask';
import {GbpLinkedAccountTask} from '/src/com/yext/listingslaunchstorm/js/enterprise/setuppublisher/gbp/gbplinkedaccounttask';
import {GbpSyncedFieldsTask} from '/src/com/yext/listingslaunchstorm/js/enterprise/setuppublisher/gbp/gbpsyncedfieldstask';
import {YelpLinkedAccountTask} from '/src/com/yext/listingslaunchstorm/js/enterprise/setuppublisher/yelp/yelplinkedaccounttask';
import {YelpSyncedFieldsTask} from '/src/com/yext/listingslaunchstorm/js/enterprise/setuppublisher/yelp/yelpsyncedfieldstask';
import {SpecifyListingConfigurationTask} from '/src/com/yext/listingslaunchstorm/js/enterprise/specifylistingconfiguration/specifylistingconfigurationtask';
import {SummaryTask} from '/src/com/yext/listingslaunchstorm/js/enterprise/summary/summarytask';
import {EnterpriseConfiguration} from '/src/com/yext/listingslaunchstorm/js/enterprise/taskflow/data/enterpriseconfiguration';
import {Task} from '/src/com/yext/listingslaunchstorm/js/enterprise/taskflow/data/task';
import {TaskGroup} from '/src/com/yext/listingslaunchstorm/js/enterprise/taskflow/data/taskgroup';
import {PageControls, useNavigation} from '/src/com/yext/listingslaunchstorm/js/enterprise/taskflow/hooks/usenavigation';
import {Button} from '/ui/components/button/button';
import {Taskflow} from '/ui/components/taskflow/taskflow';

/**
 * @param {{
 *   onSaveConfiguration: function(EnterpriseConfiguration): void,
 *   publisherIds: Array<number>,
 *   hasChooseEntitiesAndListingsConfigSetup: boolean,
 * }} props
 * @returns {React.ReactNode}
 */
export function EnterpriseTaskflow({
  onSaveConfiguration,
  publisherIds,
  hasChooseEntitiesAndListingsConfigSetup,
}) {
  const publisherTaskGroupMap = {
    [GooglePublisherId]: {
      taskGroup: new TaskGroup({
        id: 'gbpSetup',
        name: yext.msg('Google Business Profile Setup'),
        tasks: [GbpLinkedAccountTask, GbpSyncedFieldsTask],
      }),
      sortOrder: 1,
    },
    [FacebookPublisherId]: {
      taskGroup: new TaskGroup({
        id: 'facebookSetup',
        name: yext.msg('Facebook Setup'),
        tasks: [FacebookLinkedAccountTask, FacebookSyncedFieldsTask],
      }),
      sortOrder: 2,
    },
    [ApplePublisherId]: {
      taskGroup: new TaskGroup({
        id: 'appleSetup',
        name: yext.msg('Apple Setup'),
        tasks: [AppleLinkedAccountTask(ApplePublisherId)],
      }),
      sortOrder: 3,
    },
    [YelpEnterprisePublisherId]: {
      taskGroup: new TaskGroup({
        id: 'yelpSetup',
        name: yext.msg('Yelp Setup'),
        tasks: [
          YelpLinkedAccountTask(YelpEnterprisePublisherId),
          YelpSyncedFieldsTask(YelpEnterprisePublisherId),
        ],
      }),
      sortOrder: 4,
    },
    [YelpKnowledgePublisherId]: {
      taskGroup: new TaskGroup({
        id: 'yelpSetup',
        name: yext.msg('Yelp Setup'),
        tasks: [
          YelpLinkedAccountTask(YelpKnowledgePublisherId),
        ],
      }),
      sortOrder: 4,
    },
  };

  const fixedPublisherIds = preprocessPublisherIds(publisherIds);

  const hasPublisherSetupSteps = publisherId => {
    return [
      GooglePublisherId,
      FacebookPublisherId,
      ApplePublisherId,
      YelpEnterprisePublisherId,
      YelpKnowledgePublisherId,
    ].includes(publisherId);
  };

  const publisherIdsWithSetupSteps = fixedPublisherIds
    .filter(hasPublisherSetupSteps);
  const publisherTasks = publisherIdsWithSetupSteps
    .map(publisherId => publisherTaskGroupMap[publisherId])
    .filter(item => item !== undefined);

  const sortedPublisherTasks = publisherTasks
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map(item => item.taskGroup);

  const taskGroups = getTaskGroups(sortedPublisherTasks);
  const taskList = taskGroups.flatMap(tg => tg.tasks);
  const taskGroupsById = taskGroups.map(tg => tg.tasks.map(t => t.id));

  const [configuration, setConfiguration] = React.useState(EnterpriseConfiguration.empty());
  const {currentPage, pageStates, controls} =
    useNavigation(taskGroupsById);

  // This index is guaranteed to exist (currentPage will always be an ID from the task list).
  const currentTaskIndex = taskList.findIndex(t => t.id === currentPage);

  const isCurrentTaskComplete = taskList[currentTaskIndex].isComplete(configuration);
  const isCurrentSectionSkipped = taskList[currentTaskIndex].isSectionSkipped(configuration);

  // A task is "enabled" if its card can be clicked on in the left nav.
  const isTaskEnabled = task => {
    const taskGroup = taskGroupsById.find(tg => tg.includes(task.id)) ?? [];
    const skippedTask = taskList[currentTaskIndex];

    // If the current section is skipped, all tasks in the section are not clickable.
    if (isCurrentSectionSkipped && taskGroup.includes(skippedTask.id)) {
      return false;
    }

    const index = taskList.findIndex(t => t.id === task.id);
    const isDisabled = pageStates.get(task.id) === 'disabled';

    // The user can click on a disabled card if it is immediately after the current, complete one.
    const canBeNext = (isCurrentTaskComplete || isCurrentSectionSkipped) && (!isDisabled || (index == currentTaskIndex + 1));

    // The user can move to any previous step that is not disabled (such as skipped steps).
    const canBeBefore = !isDisabled && (index <= currentTaskIndex);

    return (canBeNext || canBeBefore);
  };

  return (
    <Taskflow
      breadcrumbsList={[
        {
          displayName: yext.msg('Publishers'),
          link: Routes.publishers(),
        },
        {
          displayName: yext.msg('Listings Launch'),
        },
      ]}
      sections={taskGroups.map(tg => ({
        header: tg.name,
        children: tg.tasks.map(t => t.getCard(
          configuration,
          taskList.findIndex(tlt => tlt.id === t.id) + 1,
          currentPage,
          controls.setPage,
          !isTaskEnabled(t),
          hasChooseEntitiesAndListingsConfigSetup,
          publisherIds,
        )),
      }))}
      pages={taskList.map(t => t.getPage(
        configuration,
        getConfigurationSetter(t, setConfiguration, controls),
        controls,
        hasChooseEntitiesAndListingsConfigSetup,
        publisherIds,
      ))}
      actionsBar={{
        ctas: getActionsBarCtas(
          isCurrentTaskComplete,
          isCurrentSectionSkipped,
          taskList[currentTaskIndex].id,
          pageStates,
          controls,
          () => onSaveConfiguration(configuration),
        ),
        onBack: controls.prevPage,
        onCancel: () => {},
      }}
      selectedPageId={currentPage}
    />
  );
}

/**
 * @param {Task} task
 * @param {function(EnterpriseConfiguration): void} setConfiguration
 * @param {PageControls} controls
 * @returns {function(EnterpriseConfiguration): void}
 */
function getConfigurationSetter(task, setConfiguration, controls) {
  return configuration => {
    // Some pages can be completed, and the changed to where they return to an incomplete state.
    // Check that the newest configuration for this step is complete, otherwise reset the page to
    // prevent the user from proceeding beyond an incomplete step.
    if (!task.isComplete(configuration)) {
      controls.resetPage(task.id);
    }

    // If a section is skipped, update the other tasks in the section to reflect this.
    controls.skipPage(
      true /* entireSection */,
      task.isSectionSkipped(configuration) /* skipped */,
      false, /* redirect */
    );

    setConfiguration(configuration);
  };
}

/**
 * @param {boolean} isComplete
 * @param {boolean} isSkipped
 * @param {string} currentTaskId
 * @param {Map<string, string>} pageStates
 * @param {PageControls} controls
 * @param {function(): void} onSave
 * @returns {!Array<React.ReactNode>}
 */
function getActionsBarCtas(isComplete, isSkipped, currentTaskId, pageStates, controls, onSave) {
  // The ActionsBar CTAs can be in three states:
  // - at the final (Summary) step, where the user can "Save" instead of "Continue".
  // - ready to finish (reached Summary), but not at the final step. in this case we show the
  //   normal "Continue" button as a secondary, along a "Go to Summary" primary action.
  // - all other cases, where only a "Continue" button is available to move to the next page.
  const isDisabled = !isComplete && !isSkipped;
  if (currentTaskId == SummaryTask.id) {
    return [(
      <Button key="save" context="primary" onClick={onSave}>
        {yext.msg('Save')}
      </Button>
    )];
  } else if (pageStates.get(SummaryTask.id) === 'active') {
    return [
      (
        <Button key="defaultNext" disabled={isDisabled} onClick={controls.nextPage}>
          {yext.msg('Continue')}
        </Button>
      ),
      (
        <Button
          key="return"
          context="primary"
          disabled={isDisabled}
          onClick={() => controls.setPage(SummaryTask.id)}
        >
          {yext.msg('Go to Summary')}
        </Button>
      ),
    ];
  } else {
    return [(
      <Button key="next" context="primary" disabled={isDisabled} onClick={controls.nextPage}>
        {yext.msg('Continue')}
      </Button>
    )];
  }
}


/** @returns {!Array<TaskGroup>} */
function getTaskGroups(publisherTasks) {
  return [
    new TaskGroup({
      id: 'listingsSetup',
      name: yext.msg('Listings Setup'),
      tasks: [SelectEntitiesTask, SpecifyListingConfigurationTask],
    }),
    ...publisherTasks,
    new TaskGroup({
      id: 'licenseAssignment',
      name: yext.msg('License Assignment'),
      tasks: [SelectFeaturesTask],
    }),
    new TaskGroup({
      id: 'confirmation',
      name: yext.msg('Confirmation'),
      tasks: [SummaryTask],
    }),
  ];
}

/** @returns {!Array<Number>} */
function preprocessPublisherIds(publisherIds) {
  // In case both yelps are present, we should default to only processing a single one
  if (publisherIds.includes(YelpEnterprisePublisherId)
      && publisherIds.includes(YelpKnowledgePublisherId)) {
    return publisherIds.filter(pubId => pubId != YelpKnowledgePublisherId);
  }
  return publisherIds;
}
